import { ENV_CONFIG } from '../config/environment';

// Input validation utilities
export const validateDeviceName = (name) => {
  if (!name || typeof name !== 'string') {
    return { isValid: false, error: 'Device name is required' };
  }
  
  if (!ENV_CONFIG.SECURITY.DEVICE_NAME_REGEX.test(name)) {
    return { 
      isValid: false, 
      error: 'Device name must be 3-20 characters, letters, numbers, spaces, hyphens, or underscores only' 
    };
  }
  
  return { isValid: true };
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email || !emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  return { isValid: true };
};

export const validatePassword = (password) => {
  if (!password || password.length < ENV_CONFIG.SECURITY.PASSWORD_MIN_LENGTH) {
    return { 
      isValid: false, 
      error: `Password must be at least ${ENV_CONFIG.SECURITY.PASSWORD_MIN_LENGTH} characters long` 
    };
  }
  return { isValid: true };
};

// Rate limiting utilities
export class RateLimiter {
  constructor() {
    this.attempts = new Map();
  }

  isRateLimited(identifier) {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || [];
    
    // Remove old attempts outside the rate limit window
    const recentAttempts = userAttempts.filter(
      attempt => now - attempt < ENV_CONFIG.SECURITY.RATE_LIMIT_WINDOW
    );
    
    this.attempts.set(identifier, recentAttempts);
    
    return recentAttempts.length >= ENV_CONFIG.SECURITY.MAX_LOGIN_ATTEMPTS;
  }

  recordAttempt(identifier) {
    const now = Date.now();
    const userAttempts = this.attempts.get(identifier) || [];
    userAttempts.push(now);
    this.attempts.set(identifier, userAttempts);
  }

  getRemainingTime(identifier) {
    const userAttempts = this.attempts.get(identifier) || [];
    if (userAttempts.length === 0) return 0;
    
    const oldestAttempt = Math.min(...userAttempts);
    const remainingTime = ENV_CONFIG.SECURITY.RATE_LIMIT_WINDOW - (Date.now() - oldestAttempt);
    return Math.max(0, remainingTime);
  }
}

// Session management utilities
export class SessionManager {
  constructor() {
    this.sessionTimer = null;
    this.onTimeout = null;
  }

  startSession(onTimeout) {
    this.onTimeout = onTimeout;
    this.resetSession();
  }

  resetSession() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }
    
    this.sessionTimer = setTimeout(() => {
      if (this.onTimeout) {
        this.onTimeout();
      }
    }, ENV_CONFIG.SECURITY.SESSION_TIMEOUT);
  }

  clearSession() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
  }
}

// Error handling utilities
export const getAuthErrorMessage = (error) => {
  switch (error.code) {
    case 'auth/user-not-found':
      return 'No account found with this email address';
    case 'auth/wrong-password':
      return 'Incorrect password';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/user-disabled':
      return 'This account has been disabled';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection';
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/invalid-credential':
      return 'Invalid credentials. Please check your email and password.';
    case 'auth/email-already-exists':
      return 'An account with this email already exists. Please sign in instead.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled. Please contact support.';
    case 'auth/requires-recent-login':
      return 'This operation requires recent authentication. Please sign in again.';
    case 'auth/credential-already-in-use':
      return 'This credential is already associated with a different account.';
    case 'auth/invalid-verification-code':
      return 'Invalid verification code. Please try again.';
    case 'auth/invalid-verification-id':
      return 'Invalid verification ID. Please try again.';
    case 'auth/missing-verification-code':
      return 'Verification code is required.';
    case 'auth/missing-verification-id':
      return 'Verification ID is required.';
    case 'auth/quota-exceeded':
      return 'Too many requests. Please try again later.';
    case 'auth/captcha-check-failed':
      return 'reCAPTCHA verification failed. Please try again.';
    case 'auth/invalid-phone-number':
      return 'Invalid phone number format.';
    case 'auth/missing-phone-number':
      return 'Phone number is required.';
    default:
      return `Authentication failed: ${error.message || 'Please try again'}`;
  }
};

// Security logging utilities
export const logSecurityEvent = async (event, details, db, auth) => {
  try {
    const { collection, addDoc } = await import('firebase/firestore');
    await addDoc(collection(db, 'security_logs'), {
      event,
      details,
      timestamp: new Date(),
      userId: auth.currentUser?.email || 'anonymous',
      deviceId: localStorage.getItem('deviceId'),
      userAgent: navigator.userAgent,
      ipAddress: 'client-side', // Note: Real IP would need server-side logging
    });
  } catch (error) {
    console.error('Failed to log security event:', error);
  }
};
